<template>
    <v-container fluid>
        <!-- Remove User Dialog -->
        <DeleteDialog
            v-if="selectedUser"
            :dialog.sync="removeUserFromGroupDialog"
            :item="selectedUser"
            v-on:confirmed="removeUserFromGroupConfirmed"
        >
            <template v-slot:title>Delete - {{ selectedUser.email }}</template>
            <template
                v-slot:content
            >Are you sure you want to remove {{ selectedUser.email }} from this group?</template>
        </DeleteDialog>

        <!-- Select user to group dialog -->
        <v-dialog v-model="addExistingUserDialog" persistent max-width="600px">
            <v-card>
                <v-card-title class="primary white--text">Select User</v-card-title>

                <v-card-text class="mt-4">
                    <v-form ref="addExistingUserForm">
                        <v-row align="center">
                            <!-- Existing Users -->
                            <v-col cols="12">
                                <v-autocomplete
                                    v-model="selectedExistingUserNodeId"
                                    :items="tenantUsers.edges"
                                    item-text="node.email"
                                    item-value="node.id"
                                    :loading="$apollo.queries.tenantUsers.loading"
                                    :rules="[rules.required]"
                                    label="User*"
                                    class="subtitle-2"
                                    outlined
                                >
                                    <template v-slot:append-outer>
                                        <v-btn
                                            v-if="hasPermission('add_customuser')"
                                            text
                                            small
                                            tabindex="-1"
                                        >
                                            <v-icon
                                                color="grey"
                                                @click="addNewUser"
                                                tabindex="-1"
                                            >add</v-icon>
                                        </v-btn>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="pb-5 pr-5">
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="cancelAddUserToTenantUserGroup">Cancel</v-btn>
                    <v-btn
                        outlined
                        rounded
                        color="primary"
                        :loading="isSavingUser"
                        @click="addUserToTenantUserGroup"
                    >Add</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- user dialog -->
        <TenantUserEditDialog
            :object.sync="selectedUser"
            :dialog.sync="userDialog"
            v-on:item-created="userAdded"
        ></TenantUserEditDialog>

        <!-- main body -->
        <!-- user list -->
        <v-row class="mx-0">
            <v-col cols="12" class="pt-0">
                <v-hover>
                    <template v-slot="{ hover }">
                        <v-card
                            :elevation="hover ? 6 : 1"
                            :loading="$apollo.queries.tenantUserGroup.loading"
                        >
                            <!-- Header -->
                            <v-card-title class="pb-0">
                                <v-icon color="primary" class="mx-2">group</v-icon>Users
                                <v-spacer></v-spacer>
                                <v-btn
                                    v-if="hasPermission('change_tenantusergroup')"
                                    color="primary"
                                    dark
                                    outlined
                                    rounded
                                    small
                                    @click="addExistingUserDialog=true"
                                >Add User</v-btn>
                            </v-card-title>

                            <!-- user table -->
                            <v-container fluid>
                                <v-data-table
                                    :headers="headers"
                                    :items="tenantUserGroup.group.userSet.edges"
                                    :options.sync="dataTableOptions"
                                    loading-text="Loading... Please wait"
                                    :items-per-page="defaultItemsPerPage"
                                >
                                    <template v-slot:item.node.tenantUser.isAdmin="{ item }">
                                        <v-icon
                                            v-if="item.node.tenantUser.isAdmin"
                                            color="success"
                                        >check_circle</v-icon>
                                    </template>
                                    <template v-slot:item.action="{ item }">
                                        <v-btn
                                            :disabled="!hasPermission('change_customuser')"
                                            icon
                                            outlined
                                            small
                                            color="primary"
                                            class="mr-2"
                                            @click="editUser(item.node)"
                                        >
                                            <v-icon small>edit</v-icon>
                                        </v-btn>
                                        <v-btn
                                            :disabled="!hasPermission('change_tenantusergroup')"
                                            icon
                                            outlined
                                            small
                                            color="error"
                                            @click="onRemoveUserFromGroup(item.node)"
                                        >
                                            <v-icon small>close</v-icon>
                                        </v-btn>
                                    </template>
                                </v-data-table>
                            </v-container>
                        </v-card>
                    </template>
                </v-hover>
            </v-col>
        </v-row>

        <!-- permission list -->
        <v-row class="mx-0">
            <v-col cols="12" class="pt-0">
                <v-hover>
                    <template v-slot="{ hover }">
                        <v-card
                            :elevation="hover ? 6 : 1"
                            :loading="$apollo.queries.tenantUserGroup.loading"
                        >
                            <!-- Header -->
                            <v-card-title>
                                <v-icon color="primary" class="mx-2">extension</v-icon>Permissions
                                <v-btn
                                    v-if="isEditingPermissions"
                                    text
                                    color="primary"
                                    class="ml-5"
                                    @click="selectAllPermissions(true)"
                                >Select all</v-btn>
                                <v-btn
                                    v-if="isEditingPermissions"
                                    text
                                    color="primary"
                                    @click="selectAllPermissions(false)"
                                >Deselect all</v-btn>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="primary"
                                    dark
                                    outlined
                                    rounded
                                    small
                                    v-if="hasPermission('change_tenantusergroup') && !isEditingPermissions"
                                    @click="startEditingPermissions"
                                >Edit</v-btn>
                                <v-btn
                                    text
                                    rounded
                                    :disabled="isSavingPermissions"
                                    v-if="isEditingPermissions"
                                    @click="cancelEditingPermission"
                                >Cancel</v-btn>
                                <v-btn
                                    color="primary"
                                    outlined
                                    rounded
                                    :loading="isSavingPermissions"
                                    v-if="isEditingPermissions"
                                    @click="savePermissions"
                                >Save</v-btn>
                            </v-card-title>

                            <v-card-text v-for="(sector, index) in permissionList" :key="index">
                                <v-card-title>
                                    <v-icon class="mr-3 primary--text">lock_open</v-icon>
                                    {{ sector.header }}
                                </v-card-title>

                                <v-simple-table>
                                    <template v-slot:default>
                                        <thead>
                                            <tr>
                                                <th class="text-left" width="40%">Module</th>
                                                <th>View</th>
                                                <th class="text-left">Add</th>
                                                <th class="text-left">Change</th>
                                                <th class="text-left">Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in sector.items" :key="index">
                                                <td>{{ item.name }}</td>
                                                <td>
                                                    <v-checkbox
                                                        v-model="permissions['view_' + item.model]"
                                                        :readonly="!isEditingPermissions"
                                                        :loading="isSelectingPermissions"
                                                        hide-details
                                                        class="ma-0"
                                                    ></v-checkbox>
                                                </td>
                                                <td>
                                                    <v-checkbox
                                                        v-model="permissions['add_' + item.model]"
                                                        :readonly="!isEditingPermissions"
                                                        hide-details
                                                        class="ma-0"
                                                    ></v-checkbox>
                                                </td>
                                                <td>
                                                    <v-checkbox
                                                        v-model="permissions['change_' + item.model]"
                                                        :readonly="!isEditingPermissions"
                                                        hide-details
                                                        class="ma-0"
                                                    ></v-checkbox>
                                                </td>
                                                <td>
                                                    <v-checkbox
                                                        v-model="permissions['delete_' + item.model]"
                                                        :readonly="!isEditingPermissions"
                                                        hide-details
                                                        class="ma-0"
                                                    ></v-checkbox>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </v-card-text>
                        </v-card>
                    </template>
                </v-hover>
            </v-col>
        </v-row>
    </v-container>
</template>


<script>
import helper from "@/utils/helper.js";
import gql from "graphql-tag";
import DeleteDialog from "@/components/base/DeleteDialog.vue";
import TenantUserEditDialog from "@/components/users/TenantUserEditDialog.vue";

export default {
    name: "tenant-user-group-detail-component",
    props: ["tenantUserGroupNodeId"],
    components: { DeleteDialog, TenantUserEditDialog },

    apollo: {
        tenantUserGroup: {
            query: gql`
                query tenantUserGroup($id: ID!) {
                    tenantUserGroup(id: $id) {
                        id
                        name
                        group {
                            id
                            userSet {
                                edges {
                                    node {
                                        id
                                        email
                                        firstName
                                        lastName
                                        tenantUser {
                                            id
                                            isAdmin
                                        }
                                    }
                                }
                            }
                            permissions {
                                edges {
                                    node {
                                        id
                                        codename
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    id: this.tenantUserGroupNodeId
                };
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update(data) {
                //send data to parent component
                this.$emit("item-fetched", data.tenantUserGroup);

                if (!data.tenantUserGroup) {
                    alert("Unknown group!");
                }

                data.tenantUserGroup.group.permissions.edges.forEach(item => {
                    this.permissions[item.node.codename] = true;
                });

                //return result to current componet
                return data.tenantUserGroup;
            },
            pollInterval: 60000 // ms
        },

        tenantUsers: {
            query: gql`
                query tenantUsers(
                    $first: Int
                    $last: Int
                    $before: String
                    $after: String
                ) {
                    tenantUsers(
                        first: $first
                        last: $last
                        before: $before
                        after: $after
                    ) {
                        edgeCount
                        totalCount
                        pageInfo {
                            startCursor
                            endCursor
                            hasPreviousPage
                            hasNextPage
                        }
                        edges {
                            node {
                                id
                                email
                                firstName
                                lastName
                                tenantUser {
                                    id
                                    isAdmin
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {};
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.tenantUsers,
            skip: true,
            pollInterval: 60000 // ms
        }
    },

    data() {
        return {
            isSavingUser: false,
            isSelectingPermissions: false,
            isSavingPermissions: false,
            userDialog: false,
            editGroupDialog: false,
            deleteGroupDialog: false,
            removeUserFromGroupDialog: false,

            addExistingUserDialog: false,
            selectedExistingUserNodeId: null,

            selectedUser: {},

            backedupPermissions: {},
            isEditingPermissions: false,
            allViewPermissions: false,
            // viewIndeterminate: true,

            tenantUserGroup: {
                id: null,
                name: "",
                group: {
                    userSet: {}
                },
                permissions: {}
            },

            permissionList: [
                {
                    header: "Plant Management",
                    items: [
                        { model: "plant", name: "Plants" },
                        {
                            model: "plantgrowthtemplate",
                            name: "Plant Growth Templates"
                        },
                        { model: "plantspecies", name: "Plant Species" },
                        { model: "color", name: "Colors" },
                        { model: "potsize", name: "Pot Sizes" },
                        { model: "environment", name: "Environments" },
                        { model: "tag", name: "Tags" }
                    ]
                },
                {
                    header: "Product Management",
                    items: [
                        { model: "product", name: "Products" },
                        { model: "productcategory", name: "Product Categories" }
                    ]
                },
                {
                    header: "Sales Management",
                    items: [{ model: "salesorder", name: "Orders" }]
                },
                {
                    header: "Greenhouse Management",
                    items: [
                        { model: "greenhouse", name: "Greenhouses" },
                        { model: "growthtask", name: "Growth Tasks" },
                        {
                            model: "growthtaskinventorybyplant",
                            name: "Inventory"
                        }
                    ]
                },
                {
                    header: "Planning Tool",
                    items: [
                        { model: "plannedtask", name: "Planning Tool" }
                    ]
                },
                {
                    header: "Customer & Supplier Management",
                    items: [
                        { model: "customer", name: "Customers" },
                        { model: "supplier", name: "Suppliers" }
                    ]
                },
                {
                    header: "User & Group Management",
                    items: [
                        { model: "tenantusergroup", name: "Groups" },
                        { model: "customuser", name: "Users" }
                    ]
                }
            ],

            permissions: {},

            tenantUsers: {},

            defaultItemsPerPage: 25,
            dataTableOptions: {},
            page: 1,

            search: "",

            headers: [
                {
                    text: "Email",
                    value: "node.email",
                    sortable: true
                },
                {
                    text: "First Name",
                    value: "node.firstName",
                    sotable: false
                },
                {
                    text: "Last Name",
                    value: "node.lastName",
                    sotable: false
                },
                {
                    text: "Administrator",
                    value: "node.tenantUser.isAdmin",
                    sotable: false
                },
                {
                    text: "Actions",
                    value: "action",
                    width: "120px",
                    align: "center",
                    sortable: false
                }
            ],
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                minLength: len => v =>
                    (v || "").length >= len ||
                    `Invalid character length, required ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
            }
        };
    },
    computed: {
        // totalNumModels() {
        //     return this.permissionList
        //         .map(item => {
        //             return item.items.length;
        //         })
        //         .reduce((a, b) => {
        //             return a + b;
        //         });
        // },
        // viewIndeterminate() {
        //     const _selected_view_permissions = Object.keys(this.permissions).filter(
        //         key => key.startsWith("view_") && this.permissions[key]
        //     ).length;
        //     console.log(this.totalNumModels, _selected_view_permissions)
        //     return _selected_view_permissions > 0 && _selected_view_permissions < this.totalNumModels;
        // }
    },
    watch: {
        addExistingUserDialog(val) {
            if (val) {
                this.$apollo.queries.tenantUsers.skip = false;
            }
        }

        // allViewPermissions(val) {
        //     this.setOperationPermissions("view", val);
        // }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        editTenantUserGroup() {
            this.editGroupDialog = true;
        },

        hasGroupPermission(permission) {
            return this.tenantUserGroup.group.permissions.edges.some(
                item => item.node.codename === permission
            );
        },

        addNewUser() {
            this.selectedUser = null;
            this.userDialog = true;
        },

        userAdded() {
            this.$apollo.queries.tenantUsers.refresh();
        },

        editUser(user) {
            this.selectedUser = JSON.parse(JSON.stringify(user));

            this.selectedUser.isAdmin = this.selectedUser.tenantUser.isAdmin;

            this.userDialog = true;
        },

        addUserToTenantUserGroup() {
            // check if form valid
            if (!this.$refs.addExistingUserForm.validate()) {
                return;
            }

            this.isSavingUser = true;

            const payload = {
                id: this.tenantUserGroupNodeId,
                userId: this.selectedExistingUserNodeId
            };

            // Call to the graphql mutation
            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation($input: AddUserToTenantUserGroupInput!) {
                            addUserToTenantUserGroup(input: $input) {
                                tenantUserGroup {
                                    id
                                    name
                                    group {
                                        id
                                        userSet {
                                            edges {
                                                node {
                                                    id
                                                    email
                                                    firstName
                                                    lastName
                                                    tenantUser {
                                                        id
                                                        isAdmin
                                                    }
                                                }
                                            }
                                        }
                                        permissions {
                                            edges {
                                                node {
                                                    id
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: payload
                    }
                })
                .then(() => {
                    // Result
                    this.addExistingUserDialog = false;
                    this.selectedExistingUserNodeId = null;
                    this.$refs.addExistingUserForm.resetValidation();
                })
                .catch(error => {
                    // Error
                    console.error(error);
                    // // We restore the initial user input
                    // this.newTag = newTag;
                })
                .finally(() => {
                    this.isSavingUser = false;
                });
        },

        cancelAddUserToTenantUserGroup() {
            this.selectedExistingUserNodeId = null;
            this.$refs.addExistingUserForm.resetValidation();
            this.addExistingUserDialog = false;
        },

        onRemoveUserFromGroup(user) {
            this.selectedUser = user;
            this.removeUserFromGroupDialog = true;
        },

        removeUserFromGroupConfirmed() {
            this.isSavingUser = true;

            const payload = {
                id: this.tenantUserGroupNodeId,
                userId: this.selectedUser.id
            };

            // Call to the graphql mutation
            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation($input: RemoveUserFromTenantUserGroupInput!) {
                            removeUserFromTenantUserGroup(input: $input) {
                                tenantUserGroup {
                                    id
                                    name
                                    group {
                                        id
                                        userSet {
                                            edges {
                                                node {
                                                    id
                                                    email
                                                    firstName
                                                    lastName
                                                    tenantUser {
                                                        id
                                                        isAdmin
                                                    }
                                                }
                                            }
                                        }
                                        permissions {
                                            edges {
                                                node {
                                                    id
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: payload
                    }
                })
                .then(() => {
                    // Result
                    this.removeUserFromGroupDialog = false;
                    this.selectedUser = {};
                })
                .catch(error => {
                    // Error
                    console.error(error);
                    // // We restore the initial user input
                    // this.newTag = newTag;
                })
                .finally(() => {
                    this.isSavingUser = false;
                });
        },

        selectAllPermissions(isAllow) {
            this.isSelectingPermissions = true;
            // view
            this.selectSingleOperationPermissions("view", isAllow);
            // add
            this.selectSingleOperationPermissions("add", isAllow);
            // change
            this.selectSingleOperationPermissions("change", isAllow);
            // delete
            this.selectSingleOperationPermissions("delete", isAllow);

            this.isSelectingPermissions = false;
        },

        selectSingleOperationPermissions(operation, isAllow) {
            // e.g. setOperationPermissions("view", true)

            this.permissionList.forEach(element => {
                element.items.forEach(item => {
                    this.permissions[operation + "_" + item.model] = isAllow;
                });
            });
        },

        startEditingPermissions() {
            // backup permissions
            this.backedupPermissions = JSON.parse(
                JSON.stringify(this.permissions)
            );

            this.isEditingPermissions = true;
        },

        cancelEditingPermission() {
            this.permissions = JSON.parse(
                JSON.stringify(this.backedupPermissions)
            );

            this.isEditingPermissions = false;
        },

        savePermissions() {
            const _selected_permissions = Object.keys(this.permissions).filter(
                key => this.permissions[key]
            );

            this.isSavingPermissions = true;

            const payload = {
                id: this.tenantUserGroupNodeId,
                permissions: _selected_permissions
            };

            // Call to the graphql mutation
            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation(
                            $input: UpdateTenantUserGroupPermissionsInput!
                        ) {
                            updateTenantUserGroupPermissions(input: $input) {
                                tenantUserGroup {
                                    id
                                    name
                                    group {
                                        id
                                        userSet {
                                            edges {
                                                node {
                                                    id
                                                    email
                                                    firstName
                                                    lastName
                                                    tenantUser {
                                                        id
                                                        isAdmin
                                                    }
                                                }
                                            }
                                        }
                                        permissions {
                                            edges {
                                                node {
                                                    id
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: payload
                    }
                })
                .then(() => {
                    // Result
                    this.isEditingPermissions = false;
                })
                .catch(error => {
                    // Error
                    console.error(error);
                    // // We restore the initial user input
                    // this.newTag = newTag;
                })
                .finally(() => {
                    this.isSavingPermissions = false;
                });
        }
    }
};
</script>